import React from "react";
// Consumer Modal

// Default
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

// Mui
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid2";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

// Custom
import CustomInput from "../../forms/input/Input";
import DatePickerInput from "../../forms/input/DatePickerInput";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import ContainedButton from "../../forms/button/ContainedButton";
import CustomSearchBox from "../search/SearchBox";

// Utils
import { fetchGETRequest, fetchPOSTRequest } from "../../utils/services";
import { useCustomFields } from "../../context/CustomFieldsProvider";
import { ToastState } from "../../types";

const ConsumerModal = ({
  openModal,
  setOpenModal,
  setToast,
  addOrEdit,
  nodes,
  setNodes,
  handleCustomerAndAssetSearch,
}: {
  openModal: { id: string; type: string };
  setOpenModal: (value: { id: string; type: string }) => void;
  setToast: (value: ToastState) => void;
  addOrEdit: "add" | "edit" | null;
  nodes: { id: string; type: string }[];
  setNodes: (value: { id: string; type: string }[]) => void;
  handleCustomerAndAssetSearch: () => void;
}) => {
  const [searchValue, setSearchValue] = useState({
    type: "id",
    value: "",
  });
  const [activeTab, setActiveTab] = useState("Consumer Details");
  const [contactDetails, setContactDetails] = useState<
    { type: string; value: string }[]
  >([]);
  const [contactType, setContactType] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [contactValue, setContactValue] = useState("");
  const [identityDetails, setIdentityDetails] = useState<
    { type: string; value: string }[]
  >([]);
  const [identityType, setIdentityType] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [identityValue, setIdentityValue] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [names, setNames] = useState<string[]>([]);
  const [createCustomer, setCreateCustomer] = useState<{
    id: { id: string; entityType: string };
    customerType: { value: string; label: string };
    salutation: { value: string; label: string };
    firstName: string;
    lastName: string;
    customerCreationDate: Date | null;
    customerBirthDate: Date | null;
    consumerId: string;
    mobileNumber: string;
    emailId: string;
    [key: string]: any;
  }>({
    id: { id: "", entityType: "" },
    customerType: { value: "", label: "" },
    salutation: { value: "", label: "" },
    firstName: "",
    lastName: "",
    customerCreationDate: null,
    customerBirthDate: null,
    consumerId: "",
    mobileNumber: "",
    emailId: "",
  });

  const navigate = useNavigate();

  const { toggleCustomFieldDrawer, createdFields } = useCustomFields();

  const handleTabChange = (_: unknown, newValue: string) => {
    setActiveTab(newValue);
  };

  const getCustomFields = (item: any) => {
    switch (item?.type?.label) {
      case "Date":
        return (
          <DatePickerInput
            label={item?.title}
            format={"dd/MM/yyyy"}
            placeholder={"dd/mm/yyyy"}
            value={createCustomer?.[item?.title?.toUpperCase()] || ""}
            onChange={(event) => {
              setCreateCustomer({
                ...createCustomer,
                [item?.title?.toUpperCase()]: event,
              });
            }}
          />
        );
      case "Checkbox":
        return (
          <Stack mt={"30px"}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    createCustomer?.[item?.title?.toUpperCase()] || false
                  }
                  onChange={(event) => {
                    setCreateCustomer({
                      ...createCustomer,
                      [item?.title?.toUpperCase()]: event.target.checked,
                    });
                  }}
                  sx={{
                    color: "#1976d2",
                    "&.Mui-checked": {
                      color: "#1976d2",
                    },
                  }}
                />
              }
              label={item?.title}
            />
          </Stack>
        );
      case "Text":
        return (
          <>
            <CustomInput
              label={item?.title}
              placeholder={item?.title}
              type="text"
              value={createCustomer?.[item?.title?.toUpperCase()] || ""}
              onChange={(event) => {
                setCreateCustomer({
                  ...createCustomer,
                  [item?.title?.toUpperCase()]: event.target.value,
                });
              }}
            />
          </>
        );

      case "Number":
        return (
          <>
            <CustomInput
              label={item?.title}
              placeholder={item?.title}
              type="number"
              value={createCustomer?.[item?.title?.toUpperCase()] || ""}
              onChange={(event) => {
                setCreateCustomer({
                  ...createCustomer,
                  [item?.title?.toUpperCase()]: event.target.value,
                });
              }}
            />
          </>
        );

      case "Dropdown":
        return (
          <>
            <CustomOutlinedSelect
              label={item?.title}
              placeholder={item?.title}
              options={item?.options}
              value={createCustomer?.[item?.title?.toUpperCase()] || ""}
              onChange={(event) => {
                setCreateCustomer({
                  ...createCustomer,
                  [item?.title?.toUpperCase()]: event,
                });
              }}
            />
          </>
        );

      default:
        return null;
    }
  };

  const TableContent = (data: { type: string; value: string }[]) => {
    return (
      <TableContainer sx={{ mt: "20px" }}>
        <Table sx={{ border: "1px solid #D7DAE7" }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{ paddingLeft: "10px" }}
                className={"custom_table_thead_cell"}
              >
                Event Type
              </TableCell>

              <TableCell className={"custom_table_thead_cell"}>
                Comments
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, index) => {
              return (
                <TableRow key={index}>
                  <TableCell sx={{ backgroundColor: "#D7DAE7" }}>
                    <Typography className={"custom_table_tbody_cell"}>
                      {item?.type}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography className={"custom_table_tbody_cell"}>
                      {item?.value}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const generateUniqueName = () => {
    const randomSuffix = Math.floor(Math.random() * 1000000000);
    const newName = `consumer${randomSuffix}`;
    setNames([...names, newName]);
    return newName;
  };

  const handleUpdate = async () => {
    try {
      const requestData = {
        tenantId: {
          id: "6e191ce0-a3e2-11ee-9483-cd24c515eaca",
          entityType: "TENANT",
        },
        title: generateUniqueName(),
        additionalInfo: {
          salutation: createCustomer.salutation?.value || "",
          consumerType: createCustomer.customerType?.value || "",
          firstName: createCustomer.firstName || "",
          lastName: createCustomer.lastName || "",
          createdOn: createCustomer.customerCreationDate || "",
          dob: createCustomer.customerBirthDate || "",
          status: "active",
          contacts: contactDetails || [],
          identity: identityDetails || [],
          consumerId: createCustomer.consumerId,
          mobileNumber: createCustomer.mobileNumber,
          emailId: createCustomer.emailId,
        },
        ...(createCustomer?.id &&
          createCustomer?.id?.id && {
            id: { id: createCustomer?.id?.id, entityType: "ASSET" },
          }),
      };

      if (createCustomer?.id && addOrEdit === "edit") {
        requestData.id = {
          id: createCustomer?.id?.id,
          entityType: "CUSTOMER",
        };
      }

      const response = await fetchPOSTRequest(`customer`, requestData, true);

      setNames([]);
      setIdentityDetails([]);
      setSearchValue({
        ...searchValue,
        value: response?.data?.id.id,
      });
      setContactDetails([]);
      setContactType(null);
      setContactValue("");
      setCreateCustomer({
        id: { id: "", entityType: "" },
        customerType: { value: "", label: "" },
        salutation: { value: "", label: "" },
        firstName: "",
        lastName: "",
        customerCreationDate: null,
        customerBirthDate: null,
        consumerId: "",
        mobileNumber: "",
        emailId: "",
      });
      if (addOrEdit === "add") {
        const updatedNodes = nodes?.map((node) => {
          if (node?.id?.toUpperCase() === "CONSUMER") {
            const newObj = {
              created: true,
              label: "Successfully created",
              link: "",
              title: "Consumer",
              value: response?.data?.id.id,
            };
            return {
              ...node,
              data: newObj,
            };
          }
          return node;
        });

        setNodes([...updatedNodes]);
      } else {
        handleCustomerAndAssetSearch();
      }

      setOpenModal({
        type: "",
        id: "",
      });

      if (response?.status === 200) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response?.data?.id?.id + " Update successful!",
        });
      } else {
        setToast({
          message: response?.data?.message,
          severity: "error",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error during Update request",
      });
    }
  };

  const handleCustomerSearch = async (payload?: string) => {
    let searchTerm = "";

    if (searchValue?.value.trim()) {
      searchTerm = searchValue?.value;
    } else if (payload) {
      searchTerm = payload;
    } else {
      setToast({
        message: "Please enter a valid search value.",
        severity: "error",
      });
      return;
    }

    const response = await fetchGETRequest(`v2/Customers/${searchTerm}`, {}, true);

    if (response?.data?.data) {
      const {
        dob,
        createdOn,
        firstName,
        lastName,
        salutation,
        consumerType,
        contacts,
        identity,
        consumerId,
        mobileNumber,
        emailId,
      } = response?.data?.data?.additionalInfo || {};
      setCreateCustomer({
        customerType: customerTypeOptions.find(
          (item) => item.value === consumerType,
        ) || { value: "", label: "" },
        salutation: salutationOptions.find(
          (item) => item.value === salutation,
        ) || { value: "", label: "" },
        firstName: firstName,
        lastName: lastName,
        customerCreationDate: createdOn ? new Date(createdOn) : null,
        customerBirthDate: dob ? new Date(dob) : null,
        id: response?.data?.data?.id,
        consumerId: consumerId,
        mobileNumber: mobileNumber,
        emailId: emailId,
      });
      setIdentityDetails(identity);
      setContactDetails(contacts);
    } else {
      setToast({ message: "Consumer not found", severity: "error" });
    }
    setSearchOpen(false);
  };

  useEffect(() => {
    if (openModal?.id && openModal?.type === "CONSUMER") {
      setSearchValue({ ...searchValue, value: openModal?.id });
      handleCustomerSearch(openModal?.id);
    }
  }, [openModal]);

  const addContact = () => {
    if (contactType && contactValue) {
      setContactDetails([
        ...contactDetails,
        { type: contactType?.value, value: contactValue },
      ]);
    } else {
      setToast({
        message: "Contact Type and Contact Value are required",
        severity: "error",
      });
    }
  };

  const addIdentity = () => {
    if (identityType && identityValue) {
      setIdentityDetails([
        ...identityDetails,
        { type: identityType?.value, value: identityValue },
      ]);
    } else {
      setToast({
        message: "Identity Type and Identity Value are required",
        severity: "error",
      });
    }
  };

  const validateEmail = (email: string) => {
    const regex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!regex.test(email)) {
      setToast({ message: "Invalid email address", severity: "error" });
    }
  };

  const contactTypeOptions = [
    { value: "Phone", label: "Phone" },
    { value: "Email", label: "Email" },
    { value: "Address", label: "Address" },
  ];

  const identificationTypeOptions = [
    { value: "Aadhar", label: "Aadhar" },
    { value: "DL", label: "DL" },
    { value: "SSN", label: "SSN" },
  ];

  const customerTypeOptions = [
    { value: "Personal", label: "Personal" },
    { value: "Business", label: "Business" },
  ];

  const salutationOptions = [
    { value: "Mr", label: "Mr." },
    { value: "Ms", label: "Ms." },
  ];

  const TABS = [
    "Consumer Details",
    "Contact Details",
    "Identification Details",
  ];

  return (
    <Dialog
      onClose={() => setOpenModal({ type: "", id: "" })}
      open={openModal?.type === "CONSUMER"}
      fullWidth
      maxWidth={"md"}
    >
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>Consumer</DialogTitle>
        <IconButton
          onClick={() => setOpenModal({ type: "", id: "" })}
          sx={{ marginRight: "10px" }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>

      <DialogContent dividers>
        <Card
          sx={{
            overflow: "visible",
            position: "relative",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body1" fontWeight={600}>
            Consumer ID
          </Typography>

          <Stack direction={"row"} gap={"10px"} alignItems={"center"}>
            <CustomSearchBox
              placeholder={"Enter 10 Digit"}
              width={"500px"}
              height={"40px"}
              handleSearchToggle={() => setSearchOpen(!searchOpen)}
              searchOpen={searchOpen}
              dialogBody={
                <Stack direction={"column"} gap={"20px"}>
                  <CustomInput
                    label={"Type"}
                    value={searchValue?.type}
                    onChange={(e) =>
                      setSearchValue({
                        ...searchValue,
                        type: e.target.value,
                      })
                    }
                  />
                  <CustomInput
                    label={"Value"}
                    value={searchValue?.value}
                    onChange={(e) =>
                      setSearchValue({
                        ...searchValue,
                        value: e.target.value,
                      })
                    }
                  />
                  <ContainedButton
                    label={"Submit"}
                    sx={{
                      width: "100%",
                      fontWeight: 600,
                      color: "#ffffff",
                    }}
                    onClick={handleCustomerSearch}
                  />
                </Stack>
              }
            />
          </Stack>
        </Card>

        <Card sx={{ mt: "20px" }}>
          <TabContext value={activeTab}>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "#CACDD7",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <TabList
                onChange={handleTabChange}
                scrollButtons={false}
                TabIndicatorProps={{
                  style: {
                    backgroundColor: "##1280E7",
                    height: "3px",
                  },
                }}
              >
                {TABS.map((item, index) => {
                  return (
                    <Tab
                      label={
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={"7px"}
                          fontWeight={500}
                        >
                          {item}
                        </Box>
                      }
                      key={index}
                      value={item}
                      className="consumer_right_card_list_tab"
                    />
                  );
                })}
              </TabList>

              <ContainedButton
                label={"Create custom fields"}
                sx={{
                  width: "fit-content",
                  fontWeight: 600,
                  color: "#ffffff",
                }}
                onClick={() => toggleCustomFieldDrawer("Consumer", true)}
              />
            </Box>

            <CardContent>
              <TabPanel value={TABS[0]} sx={{ padding: 0 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                    <CustomOutlinedSelect
                      label={"Consumer Type"}
                      options={customerTypeOptions}
                      placeholder={"Select"}
                      value={createCustomer?.customerType}
                      onChange={(event) => {
                        setCreateCustomer({
                          ...createCustomer,
                          customerType: event as {
                            value: string;
                            label: string;
                          },
                        });
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                    <CustomOutlinedSelect
                      label={"Salutation"}
                      options={salutationOptions}
                      placeholder={"Select"}
                      value={createCustomer?.salutation}
                      onChange={(event) => {
                        setCreateCustomer({
                          ...createCustomer,
                          salutation: event as { value: string; label: string },
                        });
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                    <CustomInput
                      label={"First Name"}
                      placeholder={"First Name"}
                      value={createCustomer?.firstName}
                      onChange={(event) => {
                        setCreateCustomer({
                          ...createCustomer,
                          firstName: event.target.value,
                        });
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                    <CustomInput
                      label={"Last Name"}
                      placeholder={"Last Name"}
                      value={createCustomer?.lastName}
                      onChange={(event) => {
                        setCreateCustomer({
                          ...createCustomer,
                          lastName: event.target.value,
                        });
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                    <DatePickerInput
                      label={"Consumer Creation Date"}
                      format={"dd/MM/yyyy"}
                      placeholder={"dd/mm/yyyy"}
                      value={createCustomer?.customerCreationDate || null}
                      onChange={(event) => {
                        setCreateCustomer({
                          ...createCustomer,
                          customerCreationDate: event,
                        });
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                    <DatePickerInput
                      label={"Consumer Birth Date"}
                      format={"dd/MM/yyyy"}
                      placeholder={"dd/mm/yyyy"}
                      value={createCustomer?.customerBirthDate || null}
                      onChange={(event) => {
                        setCreateCustomer({
                          ...createCustomer,
                          customerBirthDate: event,
                        });
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                    <CustomInput
                      label={"Consumer ID"}
                      placeholder={"Consumer ID"}
                      value={createCustomer?.consumerId}
                      name={"consumerId"}
                      onChange={(event) => {
                        setCreateCustomer({
                          ...createCustomer,
                          consumerId: event.target.value,
                        });
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                    <CustomInput
                      label={"Mobile Number"}
                      placeholder={"Mobile Number"}
                      value={createCustomer?.mobileNumber}
                      name={"mobileNumber"}
                      onChange={(event) => {
                        setCreateCustomer({
                          ...createCustomer,
                          mobileNumber: event.target.value,
                        });
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                    <CustomInput
                      label={"Email Id"}
                      placeholder={"Email Id"}
                      value={createCustomer?.emailId}
                      name={"emailId"}
                      onChange={(event) => {
                        setCreateCustomer({
                          ...createCustomer,
                          emailId: event.target.value,
                        });
                      }}
                    />
                  </Grid>
                </Grid>

                {createdFields?.["Consumer"] && (
                  <>
                    <Stack sx={{ paddingTop: "30px", paddingBottom: "20px" }}>
                      <Divider sx={{ borderWidth: "1.5px" }} />
                    </Stack>

                    <Grid container spacing={2}>
                      {createdFields?.["Consumer"]?.map(
                        (item: any, index: number) => {
                          return (
                            <Grid
                              size={{ xs: 12, md: 4, lg: 4 }}
                              mt={"10px"}
                              key={index}
                            >
                              {getCustomFields(item)}
                            </Grid>
                          );
                        },
                      )}
                    </Grid>
                  </>
                )}
              </TabPanel>

              <TabPanel value={TABS[1]} sx={{ padding: 0 }}>
                <Stack direction={"row"} justifyContent={"space-between"}>
                  <Stack width={"75%"}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 5, lg: 5 }}>
                        <CustomOutlinedSelect
                          placeholder={"Select"}
                          options={contactTypeOptions}
                          label={"Contact Type"}
                          value={contactType}
                          onChange={(newValue) => {
                            setContactType(
                              newValue as { value: string; label: string },
                            );
                          }}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 5, lg: 5 }}>
                        <CustomInput
                          label={"Contact Value"}
                          placeholder={"Contact"}
                          value={contactValue}
                          onChange={(e) => {
                            setContactValue(e.target.value);
                            if (contactType?.value === "Email") {
                              validateEmail(e.target.value);
                            }
                          }}
                          type={
                            contactType?.value === "Email"
                              ? "email"
                              : contactType?.value === "Phone"
                                ? "number"
                                : contactType?.value === "Address"
                                  ? "text"
                                  : "text"
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 2, lg: 2 }} mt={"30px"}>
                        <ContainedButton
                          label={"Add Contact"}
                          sx={{
                            width: "fit-content",
                            fontWeight: 600,
                            height: "35px",
                            color: "#ffffff",
                          }}
                          onClick={addContact}
                        />
                      </Grid>
                    </Grid>
                  </Stack>

                  {contactDetails && contactDetails?.length > 0 && (
                    <Stack width={"25%"}>{TableContent(contactDetails)}</Stack>
                  )}
                </Stack>
              </TabPanel>

              <TabPanel value={TABS[2]} sx={{ padding: 0 }}>
                <Stack direction={"row"} justifyContent={"space-between"}>
                  <Stack width={"75%"}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 5, lg: 5 }}>
                        <CustomOutlinedSelect
                          placeholder={"Select"}
                          options={identificationTypeOptions}
                          label={"Identification Type"}
                          value={identityType}
                          onChange={(newValue) => {
                            setIdentityType(
                              newValue as { value: string; label: string },
                            );
                          }}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 5, lg: 5 }}>
                        <CustomInput
                          label={"Identification Value"}
                          placeholder={"Identification"}
                          value={identityValue}
                          onChange={(e) => {
                            setIdentityValue(e.target.value);
                          }}
                          type={
                            identityType?.value === "Aadhar" ? "number" : "text"
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 2, lg: 2 }} mt={"30px"}>
                        <ContainedButton
                          label={"Add Identity"}
                          sx={{
                            width: "fit-content",
                            fontWeight: 600,
                            height: "35px",
                            color: "#ffffff",
                          }}
                          onClick={addIdentity}
                        />
                      </Grid>
                    </Grid>
                  </Stack>

                  {identityDetails && identityDetails?.length > 0 && (
                    <Stack width={"25%"}>{TableContent(identityDetails)}</Stack>
                  )}
                </Stack>
              </TabPanel>
            </CardContent>
          </TabContext>
        </Card>

        <DialogActions>
          <ContainedButton
            label={"Save"}
            sx={{
              width: "fit-content",
              fontWeight: 600,
              color: "#ffffff",
            }}
            onClick={handleUpdate}
          />
          <ContainedButton
            label={"Close"}
            bgColor={"linear-gradient(180deg, #FF7F7F 0%, #FF4D4D 100%)"}
            sx={{
              fontWeight: 600,
              color: "#ffffff",
            }}
            onClick={() => setOpenModal({ type: "", id: "" })}
          />
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default ConsumerModal;
